import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { Button } from '../../components/common/Button';
import { categoriesApi } from '../../services/api';
import { Category } from '../../types';

interface CategoryAddProps {
  onNavigate: (page: string) => void;
}

export const CategoryAdd: React.FC<CategoryAddProps> = ({ onNavigate }) => {
  const [formData, setFormData] = useState({
    name: '',
    parent_category_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await categoriesApi.getAll();
      if (response.success && response.data) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); // Clear any previous errors

    if (!formData.name.trim()) {
      setError('Please provide a category name');
      return;
    }

    setSaving(true);
    try {
      const response = await categoriesApi.create({
        name: formData.name.trim(),
        parent_category_id: formData.parent_category_id || undefined,
        // Note: description is not supported by the backend API yet
      });

      if (response.success) {
        onNavigate('categories');
      } else {
        // Handle API error response
        const errorMessage = response.message || 'Failed to create category';

        // Check if it's a duplicate error (message contains "already exists")
        if (errorMessage.toLowerCase().includes('already exists')) {
          setError(`A category with the name "${formData.name.trim()}" already exists. Please choose a different name.`);
        } else {
          setError(errorMessage);
        }
      }
    } catch (error) {
      console.error('Failed to create category:', error);
      setError('Failed to create category. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Add New Category</h2>
          <p className="text-gray-600">Create a new category to organize your products</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* Category Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Category Name *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Clothing, Electronics, Accessories"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Parent Category */}
          <div>
            <label htmlFor="parent_category_id" className="block text-sm font-medium text-gray-700 mb-2">
              Parent Category
            </label>
            <select
              id="parent_category_id"
              value={formData.parent_category_id}
              onChange={(e) => setFormData({ ...formData, parent_category_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={loadingCategories}
            >
              <option value="">None (Top-level category)</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Optional: Choose a parent category to create a subcategory
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => onNavigate('categories')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={saving}
              icon={Save}
            >
              Save Category
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};